import { useEffect, useMemo, useState, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useTenant } from "@/contexts/TenantContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Label } from "@/components/ui/label";
import { slugify } from "@/lib/utils";
import { toast } from "@/components/ui/sonner";
import { Maximize2, ArrowLeft, Trash, Loader2, Pencil } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { MDXEditor, headingsPlugin, listsPlugin, markdownShortcutPlugin, toolbarPlugin, BoldItalicUnderlineToggles, ListsToggle, BlockTypeSelect, Button as MDXToolbarButton, Separator, quotePlugin, CodeToggle, InsertTable, tablePlugin, linkPlugin, linkDialogPlugin, CreateLink, InsertAdmonition, directivesPlugin, AdmonitionDirectiveDescriptor, codeBlockPlugin, InsertThematicBreak, thematicBreakPlugin } from "@mdxeditor/editor";
import "@mdxeditor/editor/style.css";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { QdrantIndexer } from "@/services/qdrant";

type KB = { id: string; tenant_id: string; slug: string; nome: string; descricao: string | null; status: string };

export default function TrainingIACreate() {
  const { tenantId } = useTenant();
  const navigate = useNavigate();
  const { id } = useParams();
  const canUse = useMemo(() => !!tenantId, [tenantId]);
  const [form, setForm] = useState<Partial<KB>>({ status: "ativo" });
  const [activeKbId, setActiveKbId] = useState<string>("");
  const [texts, setTexts] = useState<Array<{ id: string; titulo: string | null; conteudo: string; status: string; last_indexed_at: string | null }>>([]);
  const [textForm, setTextForm] = useState<{ titulo?: string | null; conteudo?: string; status?: string }>({ status: "ativo" });
  const [fullscreenEditor, setFullscreenEditor] = useState<{ title: string; value: string; onChange: (v: string)=>void } | null>(null);
  const [editingTextId, setEditingTextId] = useState<string | null>(null);
  const [savingInsert, setSavingInsert] = useState(false);
  const [savingUpdate, setSavingUpdate] = useState(false);
  const [editorKey, setEditorKey] = useState(0);
  const editorRef = useRef<any>(null);

  useEffect(() => {
    // modo edição por rota
    if (id && tenantId) {
      (async () => {
        const { data } = await supabase.from("knowledge_bases" as any).select("*").eq("tenant_id", tenantId).eq("id", id).single();
        if (data) {
          setForm(data as any);
          setActiveKbId((data as any).id);
        }
      })();
    }
  }, [id, tenantId]);

  useEffect(() => {
    if (!tenantId || !activeKbId) return;
    const load = async () => {
      const { data: t } = await supabase
        .from("kb_texts" as any)
        .select("id,titulo,conteudo,status,last_indexed_at")
        .eq("tenant_id", tenantId)
        .eq("kb_id", activeKbId)
        .order("created_at", { ascending: false });
      setTexts((t as any[]) || []);
    };
    load();
  }, [tenantId, activeKbId]);

  const saveKb = async () => {
    if (!tenantId) return;
    const payload = { tenant_id: tenantId, slug: form.slug || (form.nome ? slugify(form.nome) : undefined), nome: form.nome, descricao: form.descricao, status: form.status || "ativo" } as any;
    if (activeKbId) {
      const { error } = await supabase.from("knowledge_bases" as any).update(payload).eq("id", activeKbId).eq("tenant_id", tenantId);
      if (error) { toast("Falha ao salvar"); return; }
      const { data } = await supabase.from("knowledge_bases" as any).select("*").eq("id", activeKbId).eq("tenant_id", tenantId).single();
      if (data) setForm(data as any);
    } else {
      const { data: ins, error } = await supabase.from("knowledge_bases" as any).insert(payload).select("*").single();
      if (error) { toast("Falha ao salvar"); return; }
      const created = ins as any;
      if (created?.id) {
        setActiveKbId(created.id);
        setForm(created as Partial<KB>);
        navigate(`/ia/treinamento-ia/${created.id}`, { replace: true });
      }
    }
  };

  const indexText = async (t: { id: string; titulo: string | null; conteudo: string }) => {
    await supabase.functions.invoke("ai-index-text", { body: { tenant_id: tenantId, kb_id: activeKbId, text_id: t.id, title: t.titulo, content: t.conteudo } } as any);
    const { data } = await supabase
      .from("kb_texts" as any)
      .select("id,titulo,conteudo,status,last_indexed_at")
      .eq("tenant_id", tenantId)
      .eq("kb_id", activeKbId)
      .order("created_at", { ascending: false });
    setTexts((data as any[]) || []);
  };

  const addTextAndIndex = async () => {
    if (!tenantId || !activeKbId || !textForm.conteudo) return;
    setSavingInsert(true);
    const payload = { tenant_id: tenantId, kb_id: activeKbId, titulo: textForm.titulo, conteudo: textForm.conteudo, status: textForm.status || "ativo" } as any;
    const { data: inserted } = await supabase
      .from("kb_texts" as any)
      .insert(payload)
      .select("id,titulo,conteudo,status,last_indexed_at")
      .single();
    const { data } = await supabase
      .from("kb_texts" as any)
      .select("id,titulo,conteudo,status,last_indexed_at")
      .eq("tenant_id", tenantId)
      .eq("kb_id", activeKbId)
      .order("created_at", { ascending: false });
    setTexts((data as any[]) || []);
    if (inserted) {
      await indexText(inserted as any);
      await QdrantIndexer.indexKbText({ tenant_id: tenantId!, kb_id: activeKbId!, text_id: (inserted as any).id, title: (inserted as any).titulo, content: (inserted as any).conteudo });
    }
    setTextForm({ titulo: "", conteudo: "", status: "ativo" });
    try { editorRef.current?.setMarkdown?.(""); } catch {}
    setEditorKey((k) => k + 1);
    setSavingInsert(false);
  };

  const deleteText = async (t: { id: string }) => {
    if (!tenantId || !activeKbId) return;
    await supabase.from("ai_search_index" as any).delete().contains("metadata", { kb_id: activeKbId, text_id: t.id }).eq("tenant_id", tenantId);
    await supabase.from("kb_texts" as any).delete().eq("tenant_id", tenantId).eq("id", t.id);
    const { data } = await supabase
      .from("kb_texts" as any)
      .select("id,titulo,conteudo,status,last_indexed_at")
      .eq("tenant_id", tenantId)
      .eq("kb_id", activeKbId)
      .order("created_at", { ascending: false });
    setTexts((data as any[]) || []);
  };

  const openEditText = (t: { id: string; titulo: string | null; conteudo: string }) => {
    setTextForm({ titulo: t.titulo || "", conteudo: t.conteudo, status: "ativo" });
    setEditingTextId(t.id);
    try { editorRef.current?.setMarkdown?.(t.conteudo || ""); } catch {}
    setEditorKey((k) => k + 1);
  };

  const saveEditFromForm = async () => {
    if (!tenantId || !activeKbId || !editingTextId || !textForm.conteudo) return;
    setSavingUpdate(true);
    const payload = { titulo: textForm.titulo, conteudo: textForm.conteudo } as any;
    const { error } = await supabase
      .from("kb_texts" as any)
      .update(payload)
      .eq("id", editingTextId)
      .eq("tenant_id", tenantId)
      .eq("kb_id", activeKbId);
    if (error) { toast("Falha ao salvar texto"); setSavingUpdate(false); return; }
    await indexText({ id: editingTextId, titulo: textForm.titulo || null, conteudo: textForm.conteudo || "" });
    await QdrantIndexer.indexKbText({ tenant_id: tenantId!, kb_id: activeKbId!, text_id: editingTextId, title: textForm.titulo || null, content: textForm.conteudo || "" });
    const { data } = await supabase
      .from("kb_texts" as any)
      .select("id,titulo,conteudo,status,last_indexed_at")
      .eq("tenant_id", tenantId)
      .eq("kb_id", activeKbId)
      .order("created_at", { ascending: false });
    setTexts((data as any[]) || []);
    setTextForm({ titulo: "", conteudo: "", status: "ativo" });
    try { editorRef.current?.setMarkdown?.(""); } catch {}
    setEditorKey((k) => k + 1);
    setEditingTextId(null);
    setSavingUpdate(false);
  };

  const cancelEditForm = () => {
    setTextForm({ status: "ativo" });
    setEditingTextId(null);
  };

  if (!canUse) {
    return (
      <div className="p-6">
        <div className="text-center space-y-2">
          <div className="text-2xl font-bold">Acesso Negado</div>
          <div className="text-muted-foreground">Nenhum tenant selecionado</div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="outline" onClick={() => navigate('/ia/treinamento-ia')}><ArrowLeft className="h-4 w-4" /></Button>
          <div>
            <h1 className="text-3xl font-bold">Novo Treinamento</h1>
            <p className="text-muted-foreground">Crie a base e adicione seus textos</p>
          </div>
        </div>
      </div>

      <Card>
        <CardContent className="pt-6">
          <div className="grid gap-3 grid-cols-8 items-end">
            <div className="space-y-1 col-span-2">
              <Label htmlFor="kb-nome">Nome</Label>
              <Input id="kb-nome" value={form.nome || ""} onChange={(e) => setForm({ ...form, nome: e.target.value, slug: slugify(e.target.value) })} />
            </div>
            <div className="space-y-1 col-span-1">
              <Label>Status</Label>
              <Select value={form.status || "ativo"} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger><SelectValue placeholder="Status" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="ativo">ativo</SelectItem>
                  <SelectItem value="inativo">inativo</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1 col-span-4">
              <Label htmlFor="kb-desc">Descrição</Label>
              <Input id="kb-desc" value={form.descricao || ""} onChange={(e) => setForm({ ...form, descricao: e.target.value })} />
            </div>
            <div className="col-span-1 flex justify-end">
              <Button onClick={saveKb}>Salvar</Button>
            </div>
          </div>
          {!activeKbId ? (
            <div className="mt-4 text-sm text-muted-foreground">Salve o treinamento para habilitar Textos.</div>
          ) : (
            <div className="mt-6 grid grid-cols-3 gap-6">
              <div className="col-span-2 space-y-3">
                <div className="space-y-1">
                  <Label htmlFor="kb-text-title">Título</Label>
                  <Input id="kb-text-title" value={textForm.titulo || ""} onChange={(e) => setTextForm({ ...textForm, titulo: e.target.value })} />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="kb-text-content">Conteúdo</Label>
                  <div className="rounded border mdx-container">
                    <MDXEditor
                      ref={editorRef}
                      key={editorKey}
                      markdown={textForm.conteudo || ""}
                      onChange={(v) => setTextForm({ ...textForm, conteudo: v })}
                      plugins={[
                        headingsPlugin({ allowedHeadingLevels: [1,2,3] }),
                        listsPlugin(),
                        markdownShortcutPlugin(),
                        quotePlugin(),
                        linkPlugin(),
                        linkDialogPlugin(),
                        tablePlugin(),
                        directivesPlugin({ directiveDescriptors: [AdmonitionDirectiveDescriptor] }),
                        codeBlockPlugin({ defaultCodeBlockLanguage: "txt" }),
                        thematicBreakPlugin(),
                        toolbarPlugin({
                          toolbarContents: () => (
                            <div className="flex items-center gap-2">
                              <BlockTypeSelect />
                              <BoldItalicUnderlineToggles />
                              <CodeToggle />
                              <ListsToggle />
                              <CreateLink />
                              <InsertTable />
                              <InsertAdmonition />
                              <InsertThematicBreak />
                              <Separator />
                              <MDXToolbarButton onClick={() => setFullscreenEditor({ title: "Conteúdo", value: textForm.conteudo || "", onChange: (v) => setTextForm({ ...textForm, conteudo: v }) })}>
                                <Maximize2 className="h-4 w-4" />
                              </MDXToolbarButton>
                            </div>
                          ),
                        }),
                      ]}
                      className="min-h-[240px]"
                    />
                  </div>
                </div>
                <div className="flex items-center justify-end gap-2">
                  {editingTextId ? (
                    <>
                      <Button onClick={saveEditFromForm} disabled={!textForm.conteudo || savingUpdate}>
                        {savingUpdate && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Salvar Texto
                      </Button>
                      <Button variant="outline" onClick={cancelEditForm} disabled={savingUpdate}>Cancelar</Button>
                    </>
                  ) : (
                    <Button onClick={addTextAndIndex} disabled={!textForm.conteudo || savingInsert}>
                      {savingInsert && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Adicionar Texto
                    </Button>
                  )}
                </div>
              </div>
              <div className="col-span-1 overflow-y-auto max-h-[50vh] pr-2">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Título</TableHead>
                      <TableHead>Ações</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {texts.map((t) => (
                      <TableRow key={`text-${t.id}`}>
                        <TableCell>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <div className="flex flex-col cursor-help">
                                <div>{t.titulo || "-"}</div>
                                <div className="text-xs text-gray-700">{t.last_indexed_at ? new Date(t.last_indexed_at).toLocaleString() : ""}</div>
                              </div>
                            </TooltipTrigger>
                            <TooltipContent className="max-w-[560px] whitespace-pre-wrap break-words">
                              {t.conteudo || ""}
                            </TooltipContent>
                          </Tooltip>
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-2">
                            <Button variant="ghost" size="icon" onClick={() => openEditText(t)} aria-label="Editar">
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button variant="ghost" size="icon" className="text-red-600 hover:text-red-700">
                                  <Trash className="h-4 w-4" />
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Excluir</AlertDialogTitle>
                                  <AlertDialogDescription>Esta ação é irreversível. Confirmar?</AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Cancelar</AlertDialogCancel>
                                  <AlertDialogAction onClick={() => deleteText(t)}>Excluir</AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {fullscreenEditor && (
        <Dialog open onOpenChange={(open) => { if (!open) setFullscreenEditor(null); }}>
          <DialogContent className="sm:max-w-[95vw] w-[95vw] h-[90vh]">
            <DialogHeader>
              <DialogTitle>{fullscreenEditor.title}</DialogTitle>
            </DialogHeader>
            <div className="rounded border mdx-container h-full">
              <MDXEditor
                markdown={fullscreenEditor.value || ""}
                onChange={(v) => setFullscreenEditor({ ...fullscreenEditor, value: v })}
                plugins={[
                  headingsPlugin({ allowedHeadingLevels: [1,2,3] }),
                  listsPlugin(),
                  markdownShortcutPlugin(),
                  quotePlugin(),
                  linkPlugin(),
                  linkDialogPlugin(),
                  tablePlugin(),
                  directivesPlugin({ directiveDescriptors: [AdmonitionDirectiveDescriptor] }),
                  codeBlockPlugin({ defaultCodeBlockLanguage: "txt" }),
                  thematicBreakPlugin(),
                  toolbarPlugin({
                    toolbarContents: () => (
                      <div className="flex items-center gap-2">
                        <BlockTypeSelect />
                        <BoldItalicUnderlineToggles />
                        <CodeToggle />
                        <ListsToggle />
                        <CreateLink />
                        <InsertTable />
                        <InsertAdmonition />
                        <InsertThematicBreak />
                      </div>
                    ),
                  }),
                ]}
                className="h-[70vh]"
              />
            </div>
            <div className="flex justify-end gap-2 mt-3">
              <Button variant="outline" onClick={() => { fullscreenEditor.onChange(fullscreenEditor.value); setFullscreenEditor(null); }}>Fechar</Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
